// src/features/analytics/components/AudienceBreakdownPanel.tsx
//
// Shared audience breakdown presentation: top-N bar chart + full table with
// the standard loading / error / honest-empty states. Used by the Audience tab
// and the Content-tab media Drawer (Seq U-5) so the two surfaces render one
// way. Data fetching stays with the callers; this is presentation only.

import { Paper, Skeleton, Table } from '@mantine/core';
import { BarChart } from '@mantine/charts';
import type { AnalyticsDimension, AnalyticsMetric } from '@/lib/constants/analytics';
import { AnalyticsUtils } from '../AnalyticsUtils';
import { DIMENSION_LABELS, chartSeriesLabel, metricValue, valueLabel } from '../audienceValues';
import type { AnalyticsEnvelope, AudienceData } from '../types';
import { AnalyticsCoverageNotice } from './AnalyticsCoverageNotice';
import { AnalyticsErrorState } from './AnalyticsErrorState';

const CHART_TOP_N = 12;

interface AudienceBreakdownPanelProps {
  dimension: AnalyticsDimension;
  metric: AnalyticsMetric;
  audience: AnalyticsEnvelope<AudienceData> | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  onRetry: () => void;
  emptyMessage: string;
}

export function AudienceBreakdownPanel({
  dimension,
  metric,
  audience,
  isLoading,
  isError,
  error,
  onRetry,
  emptyMessage,
}: AudienceBreakdownPanelProps) {
  const rows = audience?.data.rows ?? [];
  const chartData = rows.slice(0, CHART_TOP_N).map((row) => ({
    value: valueLabel(dimension, row.value),
    [chartSeriesLabel(metric)]:
      metric === 'watchTime' ? Math.round(row.watchTimeMs / 60000) : metricValue(row, metric),
  }));

  if (isError) {
    return <AnalyticsErrorState error={error} onRetry={onRetry} />;
  }
  if (isLoading) {
    return <Skeleton height={260} />;
  }
  if (rows.length === 0) {
    return (
      <AnalyticsCoverageNotice
        coverage={audience?.coverage}
        freshness={audience?.freshness}
        emptyMessage={emptyMessage}
      />
    );
  }
  return (
    <Paper withBorder p="md">
      <AnalyticsCoverageNotice coverage={audience?.coverage} freshness={audience?.freshness} />
      <BarChart
        h={220}
        data={chartData}
        dataKey="value"
        series={[{ name: chartSeriesLabel(metric), color: 'blue.6' }]}
        withLegend={false}
        mt="xs"
      />
      <Table striped highlightOnHover mt="md">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{DIMENSION_LABELS[dimension]}</Table.Th>
            <Table.Th style={{ textAlign: 'right' }}>Sessions</Table.Th>
            <Table.Th style={{ textAlign: 'right' }}>Watch time</Table.Th>
            <Table.Th style={{ textAlign: 'right' }}>Unique viewers</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row) => (
            <Table.Tr key={row.value}>
              <Table.Td>{valueLabel(dimension, row.value)}</Table.Td>
              <Table.Td style={{ textAlign: 'right' }}>
                {AnalyticsUtils.formatCount(row.sessions)}
              </Table.Td>
              <Table.Td style={{ textAlign: 'right' }}>
                {AnalyticsUtils.formatWatchTime(row.watchTimeMs)}
              </Table.Td>
              <Table.Td style={{ textAlign: 'right' }}>
                {AnalyticsUtils.formatCount(row.uniqueViewers)}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Paper>
  );
}
